import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Shield, BarChart3, X } from "lucide-react";

const KEY = "elsa_cookie_consent";
const Ctx = createContext(null);
export const useCookieConsent = () => useContext(Ctx);

const loadAnalytics = () => {
  if (typeof window !== "undefined" && typeof window.__loadAnalytics === "function") {
    window.__loadAnalytics();
  }
};

const Toggle = ({ checked, disabled, onChange, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    data-testid={testid}
    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
      checked ? "bg-[#0A3087]" : "bg-[#C7CDD9]"
    } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${checked ? "translate-x-[22px]" : "translate-x-0.5"}`} />
  </button>
);

export function CookieConsentProvider({ children }) {
  const [showBanner, setShowBanner] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [draftAnalytics, setDraftAnalytics] = useState(false);

  useEffect(() => {
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem(KEY));
    } catch {
      saved = null;
    }
    if (saved && typeof saved.analytics === "boolean") {
      setAnalytics(saved.analytics);
      setDraftAnalytics(saved.analytics);
      if (saved.analytics) loadAnalytics();
    } else {
      setShowBanner(true);
    }
  }, []);

  const persist = (a) => {
    try {
      localStorage.setItem(KEY, JSON.stringify({ analytics: a, ts: Date.now() }));
    } catch {}
    setAnalytics(a);
    setDraftAnalytics(a);
    if (a) loadAnalytics();
    setShowBanner(false);
    setShowPrefs(false);
  };

  const acceptAll = () => persist(true);
  const rejectAll = () => persist(false);
  const savePrefs = () => persist(draftAnalytics);

  const openPreferences = useCallback(() => {
    setDraftAnalytics(analytics);
    setShowBanner(false);
    setShowPrefs(true);
  }, [analytics]);

  const closePrefs = () => {
    setShowPrefs(false);
    // if user never made a first choice, bring the banner back
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem(KEY));
    } catch {}
    if (!saved) setShowBanner(true);
  };

  return (
    <Ctx.Provider value={{ openPreferences, analytics }}>
      {children}

      {/* FIRST LAYER — banner */}
      {showBanner && !showPrefs && (
        <div className="fixed inset-x-0 bottom-0 z-[100] p-4 sm:p-6" data-testid="cookie-banner">
          <div className="mx-auto max-w-[1100px] rounded-xl border border-black/10 bg-white shadow-2xl p-6 md:p-7">
            <p className="text-sm text-[#0B0B0C]/80 leading-relaxed max-w-3xl">
              Wij gebruiken cookies om onze website goed te laten werken en om, mits uw toestemming, inzicht te krijgen in het gebruik van onze website. Lees meer in ons{" "}
              <Link to="/privacybeleid" className="underline font-600 text-[#0A3087] hover:text-[#0A3087]/80" data-testid="cookie-banner-privacy-link">privacybeleid</Link>.
            </p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button onClick={acceptAll} data-testid="cookie-accept-all" className="w-full px-5 py-3 rounded-full text-white text-sm font-600 transition-transform hover:-translate-y-0.5" style={{ background: "var(--elsa-blue)" }}>
                Alles aanvaarden
              </button>
              <button onClick={rejectAll} data-testid="cookie-reject-all" className="w-full px-5 py-3 rounded-full text-white text-sm font-600 transition-transform hover:-translate-y-0.5" style={{ background: "var(--elsa-blue)" }}>
                Alles weigeren
              </button>
              <button onClick={openPreferences} data-testid="cookie-open-prefs" className="w-full px-5 py-3 rounded-full text-white text-sm font-600 transition-transform hover:-translate-y-0.5" style={{ background: "var(--elsa-blue)" }}>
                Voorkeuren instellen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SECOND LAYER — preferences */}
      {showPrefs && (
        <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-4 bg-black/50" data-testid="cookie-prefs">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-black/10">
              <h2 className="font-serif text-2xl text-[#0A3087]">Cookievoorkeuren</h2>
              <button onClick={closePrefs} data-testid="cookie-prefs-close" className="h-8 w-8 grid place-items-center rounded-md hover:bg-[#F4F5F7]"><X size={18} /></button>
            </div>

            <div className="p-6 space-y-5">
              {/* Strikt noodzakelijk */}
              <div className="flex gap-4 p-4 rounded-lg border border-black/10">
                <Shield size={22} className="mt-0.5 shrink-0" style={{ color: "var(--elsa-blue)" }} />
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-4 mb-1">
                    <h3 className="font-600 text-[#0B0B0C]">Strikt noodzakelijk</h3>
                    <Toggle checked disabled onChange={() => {}} testid="cookie-toggle-necessary" />
                  </div>
                  <p className="text-xs text-[#0B0B0C]/60 leading-relaxed">
                    Deze cookies zijn noodzakelijk voor de beveiliging en werking van de website en kunnen niet worden uitgeschakeld. <span className="text-[#0B0B0C]/40">(Cloudflare: __cf_bm, _cfvuid)</span>
                  </p>
                </div>
              </div>

              {/* Analytics */}
              <div className="flex gap-4 p-4 rounded-lg border border-black/10">
                <BarChart3 size={22} className="mt-0.5 shrink-0" style={{ color: "var(--elsa-blue)" }} />
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-4 mb-1">
                    <h3 className="font-600 text-[#0B0B0C]">Analytics</h3>
                    <Toggle checked={draftAnalytics} onChange={setDraftAnalytics} testid="cookie-toggle-analytics" />
                  </div>
                  <p className="text-xs text-[#0B0B0C]/60 leading-relaxed">
                    Deze cookies helpen ons anoniem te begrijpen hoe bezoekers onze website gebruiken, zodat we ze kunnen verbeteren. <span className="text-[#0B0B0C]/40">(PostHog)</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 pt-0">
              <button onClick={savePrefs} data-testid="cookie-save-prefs" className="w-full px-5 py-3 rounded-full text-white text-sm font-600 transition-transform hover:-translate-y-0.5" style={{ background: "var(--elsa-blue)" }}>
                Voorkeuren opslaan
              </button>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}
